/**
 * Workbox 自定义 SW 策略 (v19.0)
 * 在 vite-plugin-pwa generateSW 之外补充业务定制
 * 由 main.ts registerSW 后调用
 */
import type { Workbox } from 'workbox-window'

export interface SwState {
  status: 'idle' | 'installing' | 'waiting' | 'active' | 'redundant'
  hasUpdate: boolean
}

export function initServiceWorker(wb: Workbox, onState: (s: SwState) => void) {
  let state: SwState = { status: 'idle', hasUpdate: false }

  wb.addEventListener('installing', () => {
    state = { ...state, status: 'installing' }
    onState(state)
  })

  wb.addEventListener('waiting', () => {
    state = { ...state, status: 'waiting', hasUpdate: true }
    onState(state)
  })

  wb.addEventListener('controlling', () => {
    // 新 SW 接管后 hard reload 一次, 防止缓存混合
    window.location.reload()
  })

  wb.addEventListener('activated', (e: any) => {
    state = { ...state, status: 'active' }
    onState(state)
    if (!e.isUpdate) {
      console.info('[v19 SW] 首次激活 · PWA ready')
    }
  })

  wb.addEventListener('redundant', () => {
    state = { ...state, status: 'redundant' }
    onState(state)
  })

  // 用户接受更新
  ;(window as any).tnApplyUpdate = () => {
    wb.messageSkipWaiting()
  }

  return wb.register().catch(err => {
    console.warn('[v19 SW] 注册失败 (开发环境正常):', err)
  })
}

/**
 * 离线队列 (复用 v17.0.120 设计)
 * 业务函数写入 → online 时自动 push
 */
export class OfflineQueue {
  private key = 'tn_v19_offline_queue'
  private static idCounter = 0

  enqueue(op: { url: string; method: string; body: any }) {
    const q = this.list()
    // id = timestamp × 1000 + counter · 防同毫秒内多次 enqueue id 碰撞
    OfflineQueue.idCounter = (OfflineQueue.idCounter + 1) % 1000
    const id = Date.now() * 1000 + OfflineQueue.idCounter
    q.push({ ...op, id, ts: new Date().toISOString() })
    localStorage.setItem(this.key, JSON.stringify(q))
  }

  list() {
    try { return JSON.parse(localStorage.getItem(this.key) || '[]') } catch { return [] }
  }

  clear() { localStorage.setItem(this.key, '[]') }

  async flush() {
    const q = this.list()
    const succeeded: number[] = []
    for (const op of q) {
      try {
        await fetch(op.url, { method: op.method, body: JSON.stringify(op.body), headers: { 'Content-Type': 'application/json' } })
        succeeded.push(op.id)
      } catch { /* 仍离线 · skip */ }
    }
    const remain = q.filter((o: any) => !succeeded.includes(o.id))
    localStorage.setItem(this.key, JSON.stringify(remain))
    return { total: q.length, succeeded: succeeded.length, remain: remain.length }
  }

  // 监听 online 自动 flush
  installAutoFlush() {
    window.addEventListener('online', () => {
      this.flush().then(r => {
        if (r.succeeded > 0) {
          console.info(`[v19 OfflineQueue] 自动同步 ${r.succeeded}/${r.total}`)
        }
      })
    })
  }
}

export const offlineQueue = new OfflineQueue()
